import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import {
  IconBook as BookOpen,
  IconCheck as Check,
  IconChevronDown as ChevronDown,
  IconEye as Eye,
  IconFlag as FlagIcon,
  IconInfoCircle as Info,
  IconPaperclip as Paperclip,
  IconArrowLeft as FiArrowLeft,
  IconTrash as FiTrash2,
  IconX as FiX,
} from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { LoadingState } from '../../../global/components'
import { useClientEntities } from '../../../hooks/agents/useClientEntities'
import { useTags } from '../../../hooks/agents/useTags'
import { Feature } from '../../../core/client/features.gen'
import { useFeature, useFeatures } from '../../../core/features/FeaturesProvider'
import { useVisibleKnowledgeSites } from '../../../hooks/knowledge/useVisibleKnowledgeSites'
import { useFlags } from '../../../hooks/flags/useFlags'
import {
  AgentTracingMode,
  MAX_ROUTING_DESCRIPTION_LENGTH,
  MIN_ROUTING_EXAMPLE_QUESTIONS,
  visibleSwitchedTools,
} from '../../../types/agentTypes'
import { useLanguage } from '../../../hooks/utility/useLanguage'
import { IconSelector } from '../../selectors/IconSelector/IconSelector'
import ExampleQuestionsEditor from './ExampleQuestionsEditor'

export interface FormData {
  name: string
  description: string
  tags: string[]
  system_prompt: string
  profile_picture_url: string
  example_questions: string[]
  // Whether the agent may search documents uploaded in the conversation.
  fileSearchEnabled: boolean
  // Whether the agent may look at the pixel contents of document images
  // (figures, charts, table crops) before showing or describing them.
  imageInspectionEnabled: boolean
  // Per-source knowledge tool identifiers (`retrieve_knowledge_<source>`)
  // the agent may search. Only sources the editing user can see are offered;
  // enforcement of who may actually retrieve stays server-side.
  knowledgeToolNames: string[]
  switchedTools: string[]
  // Retrieval width ("k"): how many documents a knowledge search returns.
  // Null leaves the agent on the backend default.
  knowledgeTopK: number | null
  // Flag classifiers that do NOT apply to this agent, by flag type. Every
  // flag applies by default, so this stays empty until one is switched off.
  excludedFlagTypes: string[]
  // Phoenix tracing: always / never / only when a traced tool is invoked.
  tracing_mode: AgentTracingMode
  // Superuser-only: isPublic exposes the agent to users of clientEntityIds.
  isPublic: boolean
  clientEntityIds: number[]
  availableInMainChat: boolean
  routingDescription: string
  useClientContext: boolean
}

export interface AgentFormProps {
  mode: 'create' | 'edit'
  initialData?: Partial<FormData>
  isLoading?: boolean
  onSubmit: (data: FormData) => Promise<void>
  onDelete?: () => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  isDeleting?: boolean
  title: string
  subtitle: string
  submitButtonText: string
  // Gates the visibility controls (public toggle + client-entity picker).
  isSuperuser?: boolean
  // The main chat hands questions on itself, so it has no main-chat section,
  // and its name is the client's brand name from the client config.
  isMainChat?: boolean
}

// Flag severities as seeded in policies.yml, mapped to Chakra colour schemes.
const FLAG_SEVERITY_COLORS: Record<string, string> = {
  info: 'blue',
  warning: 'orange',
  critical: 'red',
}

// Retrieval-width bounds, mirroring DEFAULT/MIN/MAX_SIMILARITY_TOP_K in
// backend `app/knowledge/base.py` — the backend validates on write, this only
// keeps the editor from offering a value it would reject. The default is shown
// as the placeholder for sources left unset.
const DEFAULT_KNOWLEDGE_TOP_K = 5
const MIN_KNOWLEDGE_TOP_K = 1
const MAX_KNOWLEDGE_TOP_K = 50

const AgentForm = ({
  mode,
  initialData = {},
  isLoading = false,
  onSubmit,
  onDelete,
  onCancel,
  isSubmitting = false,
  isDeleting = false,
  title,
  subtitle,
  submitButtonText,
  isSuperuser = false,
  isMainChat = false,
}: AgentFormProps) => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  // Runtime feature flag gating the inspect_image tool. When off, the toggle is
  // hidden and the tool is never added — the backend gates it too, this only
  // keeps the form honest about what the deployment supports.
  const imageInspectionFeature = useFeature(Feature.IMAGE_INSPECTION)

  // Color mode values
  const inputBg = useColorModeValue('white', 'gray.700')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const [customTag, setCustomTag] = useState('')
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Hooks
  const { tags: availableTags, createTag } = useTags()
  const { clientEntities } = useClientEntities(isSuperuser)

  // React Hook Form
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    setError,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      tags: [],
      system_prompt: '',
      profile_picture_url: '',
      example_questions: [],
      fileSearchEnabled: true,
      imageInspectionEnabled: imageInspectionFeature,
      knowledgeToolNames: [],
      switchedTools: [],
      knowledgeTopK: null,
      excludedFlagTypes: [],
      tracing_mode: 'tool_based',
      isPublic: false,
      clientEntityIds: [],
      availableInMainChat: false,
      routingDescription: '',
      useClientContext: false,
    },
  })

  const watchedTags = watch('tags')
  const watchedExampleQuestions = watch('example_questions')
  const watchedKnowledgeToolNames = watch('knowledgeToolNames')
  const watchedSwitchedTools = watch('switchedTools')
  const watchedKnowledgeTopK = watch('knowledgeTopK')
  const watchedExcludedFlagTypes = watch('excludedFlagTypes')
  const watchedIsPublic = watch('isPublic')
  const watchedClientEntityIds = watch('clientEntityIds')
  const watchedAvailableInMainChat = watch('availableInMainChat')
  const watchedRoutingDescription = watch('routingDescription')
  const watchedUseClientContext = watch('useClientContext')

  const agentRoutingFeature = useFeature(Feature.AGENT_ROUTING)
  const showRouting = isSuperuser && agentRoutingFeature && !isMainChat
  // The backend enforces the same rule.
  const routingReady =
    watchedRoutingDescription.trim().length > 0 &&
    (watchedExampleQuestions ?? []).filter(question => question.trim()).length >= MIN_ROUTING_EXAMPLE_QUESTIONS

  // Sources the editing user may see, offered as per-source toggles. The
  // endpoint gates the list per user (entity scope / superuser), so a source
  // like the DKB work instructions only appears to users entitled to it.
  const { sites: knowledgeSites } = useVisibleKnowledgeSites()

  // Knowledge retrieval exists at all only behind this feature; without it the
  // retrieval-width field would configure something the agent never does.
  const internalSearchFeature = useFeature(Feature.INTERNAL_SEARCH)
  const { isEnabled } = useFeatures()

  const toggleTool = (field: 'knowledgeToolNames' | 'switchedTools', toolName: string) => {
    const current = watch(field) ?? []
    const next = current.includes(toolName) ? current.filter(name => name !== toolName) : [...current, toolName]
    setValue(field, next, { shouldDirty: true })
  }

  // Clearing the field stores null rather than today's default, so the agent
  // follows the backend default if that ever changes.
  const setKnowledgeTopK = (raw: string) => {
    const parsed = Number.parseInt(raw, 10)
    setValue(
      'knowledgeTopK',
      Number.isNaN(parsed) ? null : Math.min(MAX_KNOWLEDGE_TOP_K, Math.max(MIN_KNOWLEDGE_TOP_K, parsed)),
      { shouldDirty: true },
    )
  }

  // The flag classifiers that can be switched off for this agent (e.g. the HR
  // flag has no business firing on a technical wiki agent). All flags apply by
  // default, so the form stores the exceptions: switch on == not excluded.
  // Behind its own feature: with it off the section is hidden, the listing
  // endpoint is 404 anyway, and the backend runs every flag on every agent.
  // Exclusions already stored stay untouched — they round-trip through the
  // hidden form value — so flipping the feature back on restores them.
  const agentFlagScopeFeature = useFeature(Feature.AGENT_FLAG_SCOPE)
  const { flags } = useFlags(agentFlagScopeFeature)

  const flagSeverityLabel = (severity: string) =>
    severity === 'critical'
      ? t('createAgent.flagsSeverityCritical')
      : severity === 'warning'
      ? t('createAgent.flagsSeverityWarning')
      : t('createAgent.flagsSeverityInfo')

  const toggleFlag = (flagType: string) => {
    const excluded = watch('excludedFlagTypes') ?? []
    const next = excluded.includes(flagType) ? excluded.filter(type => type !== flagType) : [...excluded, flagType]
    setValue('excludedFlagTypes', next, { shouldDirty: true })
  }

  // Reset form when initial data changes (only for edit mode with actual data)
  useEffect(() => {
    if (mode === 'edit' && initialData && Object.keys(initialData).length > 0) {
      reset({
        name: initialData.name || '',
        description: initialData.description || '',
        tags: initialData.tags || [],
        system_prompt: initialData.system_prompt || '',
        profile_picture_url: initialData.profile_picture_url || '',
        example_questions: initialData.example_questions || [],
        fileSearchEnabled: initialData.fileSearchEnabled ?? true,
        imageInspectionEnabled: initialData.imageInspectionEnabled ?? imageInspectionFeature,
        knowledgeToolNames: initialData.knowledgeToolNames ?? [],
        switchedTools: initialData.switchedTools ?? [],
        knowledgeTopK: initialData.knowledgeTopK ?? null,
        excludedFlagTypes: initialData.excludedFlagTypes ?? [],
        tracing_mode: initialData.tracing_mode || 'tool_based',
        isPublic: initialData.isPublic ?? false,
        clientEntityIds: initialData.clientEntityIds ?? [],
        availableInMainChat: initialData.availableInMainChat ?? false,
        routingDescription: initialData.routingDescription ?? '',
        useClientContext: initialData.useClientContext ?? false,
      })
    }
  }, [mode, initialData, reset, imageInspectionFeature])

  // When adding a custom tag, create it in the backend if it doesn't exist, then add to form state
  const handleAddCustomTag = async () => {
    const trimmed = customTag.trim()
    if (!trimmed || watchedTags.includes(trimmed)) return
    // Check if tag already exists in availableTags
    const exists = availableTags.some((t: string) => t === trimmed)
    if (!exists) {
      try {
        await createTag({ name: trimmed })
      } catch (e) {
        // Optionally show error (e.g., toast)
        return
      }
    }
    setValue('tags', [...watchedTags, trimmed], { shouldDirty: true })
    setCustomTag('')
  }

  // Remove by tag name
  const handleRemoveTag = (tagToRemove: string) => {
    setValue(
      'tags',
      watchedTags.filter(tag => tag !== tagToRemove),
      { shouldDirty: true },
    )
  }

  // On submit, pass tag names as strings
  const handleFormSubmit = async (data: FormData) => {
    // With the section hidden there is no field for the error (nor a backend rule).
    if (showRouting && data.availableInMainChat && !routingReady) {
      setError('routingDescription', {
        type: 'routing',
        message: t('createAgent.routingNotReady', { min: MIN_ROUTING_EXAMPLE_QUESTIONS }),
      })
      return
    }
    await onSubmit({ ...data, tags: watchedTags })
  }

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete()
    }
  }

  const handleBackClick = () => {
    if (isDirty) {
      onOpen()
    } else {
      if (onCancel) {
        onCancel()
      } else {
        navigate('/library')
      }
    }
  }

  const confirmBack = () => {
    onClose()
    if (onCancel) {
      onCancel()
    } else {
      navigate('/library')
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <LoadingState
        message={mode === 'edit' ? t('editAgent.loadingAgent') : t('common.loading')}
        fullHeight
        variant='spinner'
        size='lg'
      />
    )
  }

  return (
    <Box
      minH='100vh'
      bg='chat.canvas'
      sx={{
        '& input, & textarea, & select': {
          borderWidth: '1px',
          borderColor: 'chat.border',
          borderRadius: 'chat.md',
          bg: 'chat.canvas',
          fontSize: 'sm',
        },
        '& input:focus-visible, & textarea:focus-visible': {
          borderColor: 'chat.focus',
          boxShadow: '0 0 0 2px var(--chakra-colors-chat-selected)',
        },
        '& label': { fontSize: 'sm', fontWeight: 500 },
        '& h2': { color: 'chat.ink', letterSpacing: '-0.4px' },
        '& hr': { borderColor: 'chat.border' },
        '& .chakra-switch__track[data-checked]': { bg: 'chat.action' },
        '& table': { fontSize: 'sm' },
        '& [data-tools-table] th, & [data-tools-table] td': { px: { base: 1.5, md: 4 } },
        '& [data-tools-table] th': { fontSize: { base: '9px', md: '11px' } },
        '& [data-tools-table] label': { fontSize: { base: 'xs', md: 'sm' } },
        '& [data-tools-table] .chakra-stack': { gap: { base: 1, md: 3 } },
        '& th': { fontSize: '11px', fontWeight: 500, letterSpacing: '0.4px' },
      }}
    >
      <Container maxW='820px' py={6} px={{ base: 4, md: 8 }}>
        {/* Header */}
        <HStack spacing={3} mb={6}>
          <IconButton
            aria-label='Go back'
            icon={<FiArrowLeft />}
            variant='ghost'
            size='lg'
            onClick={handleBackClick}
            color='app.text.primary'
          />
          <Box flex={1}>
            <Heading size='lg' fontSize='chat.title' color='chat.ink'>
              {title}
            </Heading>
            <Text color='app.text.secondary' fontSize='sm' mt={1}>
              {subtitle}
            </Text>
          </Box>
          {mode === 'edit' && onDelete && (
            <Button leftIcon={<FiTrash2 />} colorScheme='red' variant='outline' onClick={onDeleteOpen} size='md'>
              {t('createAgent.deleteAssistant')}
            </Button>
          )}
        </HStack>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <VStack spacing={6} align='stretch'>
            {/* Single Form Card */}
            <Card bg='chat.canvas' boxShadow='none'>
              <CardBody p={0}>
                <VStack spacing={6} align='stretch'>
                  {/* Basic Information Section */}
                  <VStack spacing={4} align='stretch'>
                    <Heading size='md' color='app.text.primary' fontWeight='bold'>
                      {t('createAgent.basicInfo')}
                    </Heading>

                    <HStack spacing={4} align='start'>
                      {/* Icon */}
                      <Box>
                        <FormLabel color='app.text.primary' mb={3}>
                          {t('createAgent.icon')} *
                        </FormLabel>
                        <Controller
                          name='profile_picture_url'
                          control={control}
                          render={({ field }) => (
                            <IconSelector selectedIcon={field.value} onIconSelect={field.onChange} />
                          )}
                        />
                      </Box>

                      {/* Name */}
                      <FormControl isRequired isInvalid={!!errors.name} flex={1}>
                        <FormLabel color='app.text.primary'>{t('createAgent.name')}</FormLabel>
                        <Input
                          {...register('name', {
                            required: t('createAgent.fillAllRequiredFields'),
                            minLength: { value: 2, message: 'Name must be at least 2 characters' },
                          })}
                          placeholder={t('createAgent.namePlaceholder')}
                          isReadOnly={isMainChat}
                          bg={inputBg}
                          border='2px solid'
                          borderColor='gray.200'
                          _hover={{ borderColor: 'gray.300' }}
                          _focus={{
                            borderColor: 'primaryScheme.500',
                            boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                          }}
                        />
                        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                      </FormControl>
                    </HStack>

                    {/* Description */}
                    <FormControl isRequired isInvalid={!!errors.description}>
                      <FormLabel color='app.text.primary'>{t('createAgent.description')}</FormLabel>
                      <Textarea
                        {...register('description', {
                          required: t('createAgent.fillAllRequiredFields'),
                          minLength: { value: 10, message: 'Description must be at least 10 characters' },
                        })}
                        placeholder={t('createAgent.descriptionPlaceholder')}
                        rows={3}
                        bg={inputBg}
                        border='2px solid'
                        borderColor='gray.200'
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'primaryScheme.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                        }}
                        resize='vertical'
                      />
                      <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
                    </FormControl>
                  </VStack>

                  <Divider />

                  {/* Tools Section */}
                  <VStack spacing={4} align='stretch'>
                    <Heading size='md' color='app.text.primary' fontWeight='bold'>
                      {t('createAgent.tools')}
                    </Heading>

                    <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' bg={inputBg} overflowX='auto'>
                      <Table data-tools-table variant='simple' size='sm'>
                        <Thead>
                          <Tr>
                            <Th color='app.text.secondary'>{t('createAgent.toolsColumnName')}</Th>
                            <Th color='app.text.secondary' textAlign='center'>
                              <HStack spacing={1} justify='center'>
                                <Text as='span'>{t('createAgent.toolsColumnTraced')}</Text>
                                <Tooltip label={t('createAgent.toolsTracedHelp')} hasArrow placement='top'>
                                  <Box as='span' color='app.text.secondary' cursor='help'>
                                    <Info size={14} />
                                  </Box>
                                </Tooltip>
                              </HStack>
                            </Th>
                            <Th color='app.text.secondary' textAlign='end'>
                              {t('createAgent.toolsColumnEnabled')}
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td>
                              <HStack spacing={3}>
                                <Box color='primaryScheme.500'>
                                  <Paperclip size={18} />
                                </Box>
                                <FormLabel htmlFor='fileSearchEnabled' mb={0} color='app.text.primary'>
                                  {t('createAgent.toolsFileSearch')}
                                </FormLabel>
                              </HStack>
                            </Td>
                            <Td textAlign='center'>
                              <Box as='span' color='primaryScheme.500' display='inline-flex'>
                                <Check size={18} />
                              </Box>
                            </Td>
                            <Td textAlign='end'>
                              <Switch
                                id='fileSearchEnabled'
                                size='lg'
                                colorScheme='primaryScheme'
                                isChecked={watch('fileSearchEnabled')}
                                onChange={e =>
                                  setValue('fileSearchEnabled', e.target.checked, { shouldDirty: true })
                                }
                              />
                            </Td>
                          </Tr>

                          {imageInspectionFeature && (
                            <Tr>
                              <Td>
                                <HStack spacing={3}>
                                  <Box color='primaryScheme.500'>
                                    <Eye size={18} />
                                  </Box>
                                  <FormLabel htmlFor='imageInspectionEnabled' mb={0} color='app.text.primary'>
                                    {t('createAgent.toolsImageInspection')}
                                  </FormLabel>
                                </HStack>
                              </Td>
                              <Td textAlign='center'>
                                <Box as='span' color='primaryScheme.500' display='inline-flex'>
                                  <Check size={18} />
                                </Box>
                              </Td>
                              <Td textAlign='end'>
                                <Switch
                                  id='imageInspectionEnabled'
                                  size='lg'
                                  colorScheme='primaryScheme'
                                  isChecked={watch('imageInspectionEnabled')}
                                  onChange={e =>
                                    setValue('imageInspectionEnabled', e.target.checked, { shouldDirty: true })
                                  }
                                />
                              </Td>
                            </Tr>
                          )}

                          {visibleSwitchedTools(isEnabled).map(({ name, labelKey }) => (
                            <Tr key={name}>
                              <Td>
                                <HStack spacing={3}>
                                  <Box color='primaryScheme.500'>
                                    <BookOpen size={18} />
                                  </Box>
                                  <FormLabel htmlFor={`tool-${name}`} mb={0} color='app.text.primary'>
                                    {t(labelKey)}
                                  </FormLabel>
                                </HStack>
                              </Td>
                              <Td textAlign='center'>
                                <Box as='span' color='primaryScheme.500' display='inline-flex'>
                                  <Check size={18} />
                                </Box>
                              </Td>
                              <Td textAlign='end'>
                                <Switch
                                  id={`tool-${name}`}
                                  size='lg'
                                  colorScheme='primaryScheme'
                                  isChecked={(watchedSwitchedTools ?? []).includes(name)}
                                  onChange={() => toggleTool('switchedTools', name)}
                                />
                              </Td>
                            </Tr>
                          ))}

                          {/* Per-source knowledge tools — only the sources the
                              editing user is allowed to see are listed. */}
                          {knowledgeSites.map(site => {
                            const enabled = (watchedKnowledgeToolNames ?? []).includes(site.tool_name)
                            return (
                              <Tr key={site.tool_name}>
                                <Td>
                                  <HStack spacing={3}>
                                    <Box color='primaryScheme.500'>
                                      <BookOpen size={18} />
                                    </Box>
                                    <FormLabel
                                      htmlFor={`knowledge-${site.tool_name}`}
                                      mb={0}
                                      color='app.text.primary'
                                    >
                                      {t('createAgent.toolsKnowledgeSource', { name: site.name })}
                                    </FormLabel>
                                  </HStack>
                                </Td>
                                <Td textAlign='center'>
                                  <Box as='span' color='primaryScheme.500' display='inline-flex'>
                                    <Check size={18} />
                                  </Box>
                                </Td>
                                <Td textAlign='end'>
                                  <Switch
                                    id={`knowledge-${site.tool_name}`}
                                    size='lg'
                                    colorScheme='primaryScheme'
                                    isChecked={enabled}
                                    onChange={() => toggleTool('knowledgeToolNames', site.tool_name)}
                                  />
                                </Td>
                              </Tr>
                            )
                          })}
                        </Tbody>
                      </Table>
                    </Box>

                    {/* Retrieval width for every knowledge search this agent
                        does — the per-source tools and the aggregate one.
                        Only shown where internal search exists at all. */}
                    {internalSearchFeature && (
                      <FormControl maxW='sm'>
                        <FormLabel color='app.text.primary'>{t('createAgent.toolsTopK')}</FormLabel>
                        <Input
                          type='number'
                          min={MIN_KNOWLEDGE_TOP_K}
                          max={MAX_KNOWLEDGE_TOP_K}
                          placeholder={String(DEFAULT_KNOWLEDGE_TOP_K)}
                          value={watchedKnowledgeTopK ?? ''}
                          onChange={e => setKnowledgeTopK(e.target.value)}
                          bg={inputBg}
                          border='2px solid'
                          borderColor='gray.200'
                          _hover={{ borderColor: 'gray.300' }}
                          _focus={{
                            borderColor: 'primaryScheme.500',
                            boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                          }}
                        />
                        <FormHelperText color='app.text.secondary'>{t('createAgent.toolsTopKHelp')}</FormHelperText>
                      </FormControl>
                    )}
                  </VStack>

                  {/* Flags Section — which flag classifiers apply to this
                      agent. Every flag applies unless switched off here, so
                      the form only stores the exceptions. Hidden when the
                      feature is off or the deployment has no flags. */}
                  {agentFlagScopeFeature && flags.length > 0 && (
                    <>
                      <Divider />

                      <VStack spacing={4} align='stretch'>
                        <Box>
                          <Heading size='md' color='app.text.primary' fontWeight='bold'>
                            {t('createAgent.flags')}
                          </Heading>
                          <Text color='app.text.secondary' fontSize='sm' mt={1}>
                            {t('createAgent.flagsHelp')}
                          </Text>
                        </Box>

                        <Box
                          borderWidth='1px'
                          borderColor='gray.200'
                          borderRadius='md'
                          bg={inputBg}
                          overflowX='auto'
                        >
                          <Table variant='simple' size='md'>
                            <Thead>
                              <Tr>
                                <Th color='app.text.secondary'>{t('createAgent.flagsColumnName')}</Th>
                                <Th color='app.text.secondary' textAlign='center'>
                                  {t('createAgent.flagsColumnSeverity')}
                                </Th>
                                <Th color='app.text.secondary' textAlign='end'>
                                  {t('createAgent.flagsColumnEnabled')}
                                </Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {flags.map(flag => (
                                <Tr key={flag.type}>
                                  <Td>
                                    <HStack spacing={3}>
                                      <Box color='primaryScheme.500'>
                                        <FlagIcon size={18} />
                                      </Box>
                                      <FormLabel htmlFor={`flag-${flag.type}`} mb={0} color='app.text.primary'>
                                        {flag.display_name}
                                      </FormLabel>
                                    </HStack>
                                  </Td>
                                  <Td textAlign='center'>
                                    <Badge colorScheme={FLAG_SEVERITY_COLORS[flag.severity]}>
                                      {flagSeverityLabel(flag.severity)}
                                    </Badge>
                                  </Td>
                                  <Td textAlign='end'>
                                    <Switch
                                      id={`flag-${flag.type}`}
                                      size='lg'
                                      colorScheme='primaryScheme'
                                      isChecked={!(watchedExcludedFlagTypes ?? []).includes(flag.type)}
                                      onChange={() => toggleFlag(flag.type)}
                                    />
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      </VStack>
                    </>
                  )}

                  <Divider />

                  {/* Tracing Section */}
                  <VStack spacing={4} align='stretch'>
                    <Box>
                      <Heading size='md' color='app.text.primary' fontWeight='bold'>
                        {t('createAgent.tracing')}
                      </Heading>
                      <Text color='app.text.secondary' fontSize='sm' mt={1}>
                        {t('createAgent.tracingHelp')}
                      </Text>
                    </Box>

                    <FormControl borderWidth='1px' borderColor='gray.200' borderRadius='md' p={4} bg={inputBg}>
                      <Controller
                        name='tracing_mode'
                        control={control}
                        render={({ field }) => (
                          <RadioGroup
                            value={field.value}
                            onChange={value => field.onChange(value as AgentTracingMode)}
                            colorScheme='primaryScheme'
                          >
                            <HStack spacing={8}>
                              <Radio value='always'>{t('createAgent.tracingAlways')}</Radio>
                              <Radio value='never'>{t('createAgent.tracingNever')}</Radio>
                              <Radio value='tool_based'>{t('createAgent.tracingToolBased')}</Radio>
                            </HStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </VStack>

                  {/* Visibility Section (superuser-only) */}
                  {isSuperuser && (
                    <>
                      <Divider />
                      <VStack spacing={4} align='stretch'>
                        <Box>
                          <Heading size='md' color='app.text.primary' fontWeight='bold'>
                            {t('createAgent.visibility')}
                          </Heading>
                          <Text color='app.text.secondary' fontSize='sm' mt={1}>
                            {t('createAgent.visibilityHelp')}
                          </Text>
                        </Box>

                        <FormControl borderWidth='1px' borderColor='gray.200' borderRadius='md' p={4} bg={inputBg}>
                          <HStack justify='space-between'>
                            <FormLabel htmlFor='isPublic' mb={0} color='app.text.primary'>
                              {t('createAgent.visibilityPublic')}
                            </FormLabel>
                            <Switch
                              id='isPublic'
                              size='lg'
                              colorScheme='primaryScheme'
                              isChecked={watchedIsPublic}
                              onChange={e => setValue('isPublic', e.target.checked, { shouldDirty: true })}
                            />
                          </HStack>
                        </FormControl>

                        {watchedIsPublic && (
                          <FormControl>
                            <FormLabel color='app.text.primary'>{t('createAgent.visibilityEntities')}</FormLabel>
                            <Menu closeOnSelect={false}>
                              <MenuButton
                                as={Button}
                                rightIcon={<ChevronDown size={16} />}
                                variant='outline'
                                bg={inputBg}
                                w='full'
                                textAlign='left'
                                fontWeight='normal'
                              >
                                {watchedClientEntityIds.length > 0
                                  ? clientEntities
                                      .filter(e => watchedClientEntityIds.includes(e.id))
                                      .map(e => e.name)
                                      .join(', ')
                                  : t('createAgent.visibilityEntitiesPlaceholder')}
                              </MenuButton>
                              <MenuList maxH='300px' overflowY='auto'>
                                <MenuOptionGroup
                                  type='checkbox'
                                  value={watchedClientEntityIds.map(String)}
                                  onChange={value => {
                                    const ids = (Array.isArray(value) ? value : [value]).map(Number)
                                    setValue('clientEntityIds', ids, { shouldDirty: true })
                                  }}
                                >
                                  {clientEntities.map(entity => (
                                    <MenuItemOption key={entity.id} value={String(entity.id)}>
                                      {entity.name}
                                    </MenuItemOption>
                                  ))}
                                </MenuOptionGroup>
                              </MenuList>
                            </Menu>
                            <FormHelperText color='app.text.secondary'>
                              {t('createAgent.visibilityEntitiesHelp')}
                            </FormHelperText>
                          </FormControl>
                        )}
                      </VStack>
                    </>
                  )}

                  {/* Main chat routing (superuser-only, behind agent_routing) */}
                  {showRouting && (
                    <>
                      <Divider />
                      <VStack spacing={4} align='stretch'>
                        <Box>
                          <Heading size='md' color='app.text.primary' fontWeight='bold'>
                            {t('createAgent.routing')}
                          </Heading>
                          <Text color='app.text.secondary' fontSize='sm' mt={1}>
                            {t('createAgent.routingHelp')}
                          </Text>
                        </Box>

                        <FormControl isInvalid={!!errors.routingDescription}>
                          <FormLabel color='app.text.primary'>{t('createAgent.routingDescription')}</FormLabel>
                          <Textarea
                            {...register('routingDescription', {
                              maxLength: {
                                value: MAX_ROUTING_DESCRIPTION_LENGTH,
                                message: t('createAgent.routingDescriptionTooLong', {
                                  max: MAX_ROUTING_DESCRIPTION_LENGTH,
                                }),
                              },
                            })}
                            placeholder={t('createAgent.routingDescriptionPlaceholder')}
                            maxLength={MAX_ROUTING_DESCRIPTION_LENGTH}
                            rows={5}
                            bg={inputBg}
                            border='2px solid'
                            borderColor='gray.200'
                            resize='vertical'
                          />
                          <FormHelperText color='app.text.secondary'>
                            {t('createAgent.routingDescriptionHelp', {
                              count: watchedRoutingDescription.length,
                              max: MAX_ROUTING_DESCRIPTION_LENGTH,
                            })}
                          </FormHelperText>
                          <FormErrorMessage>{errors.routingDescription?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl borderWidth='1px' borderColor='gray.200' borderRadius='md' p={4} bg={inputBg}>
                          <HStack justify='space-between'>
                            <FormLabel htmlFor='availableInMainChat' mb={0} color='app.text.primary'>
                              {t('createAgent.routingAvailable')}
                            </FormLabel>
                            <Switch
                              id='availableInMainChat'
                              size='lg'
                              colorScheme='primaryScheme'
                              isChecked={watchedAvailableInMainChat}
                              isDisabled={!routingReady && !watchedAvailableInMainChat}
                              onChange={e =>
                                setValue('availableInMainChat', e.target.checked, { shouldDirty: true })
                              }
                            />
                          </HStack>
                          {!routingReady && (
                            <FormHelperText color='app.text.secondary'>
                              {t('createAgent.routingNotReady', { min: MIN_ROUTING_EXAMPLE_QUESTIONS })}
                            </FormHelperText>
                          )}
                        </FormControl>

                        <FormControl borderWidth='1px' borderColor='gray.200' borderRadius='md' p={4} bg={inputBg}>
                          <HStack justify='space-between'>
                            <FormLabel htmlFor='useClientContext' mb={0} color='app.text.primary'>
                              {t('createAgent.routingUseClientContext')}
                            </FormLabel>
                            <Switch
                              id='useClientContext'
                              size='lg'
                              colorScheme='primaryScheme'
                              isChecked={watchedUseClientContext}
                              onChange={e => setValue('useClientContext', e.target.checked, { shouldDirty: true })}
                            />
                          </HStack>
                          <FormHelperText color='app.text.secondary'>
                            {t('createAgent.routingUseClientContextHelp')}
                          </FormHelperText>
                        </FormControl>
                      </VStack>
                    </>
                  )}

                  {/* Tags Section */}
                  <VStack spacing={4} align='stretch'>
                    <Box>
                      <HStack>
                        <Heading size='md' color='app.text.primary' fontWeight='bold'>
                          {t('createAgent.tags')}
                        </Heading>
                        <Badge variant='outline' colorScheme='gray' size='sm'>
                          {t('createAgent.optional')}
                        </Badge>
                      </HStack>
                      <Text color='app.text.secondary' fontSize='sm' mt={1}>
                        {t('createAgent.tagsHelp')}
                      </Text>
                    </Box>

                    {/* Add Custom Tag */}
                    <HStack spacing={4}>
                      <Input
                        placeholder={t('createAgent.addNewTag')}
                        value={customTag}
                        onChange={e => setCustomTag(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleAddCustomTag()}
                        bg={inputBg}
                        color='app.text.primary'
                        border='2px solid'
                        borderColor='gray.200'
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'primaryScheme.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                        }}
                        size='sm'
                      />
                      <Button
                        onClick={handleAddCustomTag}
                        colorScheme='primaryScheme'
                        isDisabled={!customTag.trim()}
                        type='button'
                        size='sm'
                      >
                        {t('createAgent.add')}
                      </Button>
                    </HStack>

                    {/* Selected Tags */}
                    {watchedTags && watchedTags.length > 0 && (
                      <Box>
                        <Text color='app.text.primary' fontWeight='medium' mb={2} fontSize='sm'>
                          Selected tags
                        </Text>
                        <Wrap spacing={3}>
                          {watchedTags.map(tag => (
                            <WrapItem key={tag}>
                              <Badge
                                colorScheme='primaryScheme'
                                variant='solid'
                                px={2}
                                py={1}
                                borderRadius='full'
                                display='flex'
                                alignItems='center'
                                gap={1}
                                fontSize='xs'
                                textTransform='none'
                              >
                                {tag}
                                <IconButton
                                  aria-label={`Remove ${tag}`}
                                  icon={<FiX />}
                                  size='xs'
                                  variant='ghost'
                                  color='white'
                                  _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                                  onClick={() => handleRemoveTag(tag)}
                                  type='button'
                                />
                              </Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    )}
                  </VStack>

                  <Divider />

                  {/* Example Questions Section */}
                  <ExampleQuestionsEditor
                    questions={watchedExampleQuestions}
                    addExampleQuestion={(question: string) => {
                      setValue('example_questions', [...watchedExampleQuestions, question], { shouldDirty: true })
                    }}
                    removeExampleQuestion={(question: string) => {
                      setValue(
                        'example_questions',
                        watchedExampleQuestions.filter(q => q !== question),
                        { shouldDirty: true },
                      )
                    }}
                  />

                  <Divider />

                  {/* Instructions Section */}
                  <VStack spacing={4} align='stretch'>
                    <Box>
                      <Heading size='md' color='app.text.primary' fontWeight='bold'>
                        {t('createAgent.instructions')}
                      </Heading>
                      <Text color='app.text.secondary' fontSize='sm' mt={1}>
                        {t('createAgent.instructionsDescription')}
                      </Text>
                    </Box>

                    <FormControl isRequired isInvalid={!!errors.system_prompt}>
                      <FormLabel color='app.text.primary'>{t('createAgent.instructionsLabel')}</FormLabel>
                      <Textarea
                        {...register('system_prompt', {
                          required: t('createAgent.fillAllRequiredFields'),
                          minLength: { value: 20, message: t('createAgent.instructionsMinLength') },
                        })}
                        placeholder={t('createAgent.instructionsPlaceholder')}
                        rows={10}
                        bg={inputBg}
                        border='2px solid'
                        borderColor='gray.200'
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'primaryScheme.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                        }}
                        resize='vertical'
                      />
                      <FormHelperText color='app.text.secondary'>
                        {t('createAgent.instructionsHelperText')}
                      </FormHelperText>
                      <FormErrorMessage>{errors.system_prompt?.message}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Action Buttons */}
            <HStack justify='space-between' pt={2} spacing={4}>
              <Button variant='outline' onClick={handleBackClick} size='lg' type='button'>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme='primaryScheme'
                type='submit'
                isLoading={isSubmitting}
                loadingText='Saving...'
                size='lg'
                px={8}
              >
                {submitButtonText}
              </Button>
            </HStack>
          </VStack>
        </form>
      </Container>

      {/* Unsaved Changes Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('createAgent.unsavedChanges')}</ModalHeader>
          <ModalBody>
            <Text>{t('createAgent.unsavedChangesMessage')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant='ghost' mr={3} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button colorScheme='red' onClick={confirmBack}>
              {t('common.back')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog (only in edit mode) */}
      {mode === 'edit' && onDelete && (
        <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                {t('createAgent.deleteAssistant')}
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this agent? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  colorScheme='red'
                  onClick={handleDelete}
                  ml={3}
                  isLoading={isDeleting}
                  loadingText='Deleting...'
                >
                  {t('createAgent.deleteAssistant')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </Box>
  )
}

export default AgentForm
